import { createPortal } from 'react-dom'
import { useEffect, useId, useRef, type CSSProperties, type ReactNode } from 'react'
import { cx } from './cx.js'
import { IconButton } from './buttons.js'

// Dialog / drawer / sheet ------------------------------------------------------------

export type DialogSide = 'center' | 'right' | 'bottom'

/** Layout classes per side. Under 640px a centered dialog is full screen and a drawer is a bottom sheet. */
export function dialogPanelClasses(side: DialogSide): string {
  if (side === 'right')
    return 'rh-sheet inset-x-0 bottom-0 max-h-[90dvh] w-full rounded-t-xl pb-[env(safe-area-inset-bottom)] sm:inset-x-auto sm:bottom-auto sm:inset-y-3 sm:right-3 sm:max-h-none sm:max-w-[var(--dlg-w)] sm:rounded-xl sm:pb-0 rh-drawer'
  if (side === 'bottom')
    return 'rh-sheet inset-x-0 bottom-0 max-h-[90dvh] w-full rounded-t-xl pb-[env(safe-area-inset-bottom)] sm:left-1/2 sm:max-w-[var(--dlg-w)] sm:-translate-x-1/2'
  return 'rh-fullscreen inset-0 h-[100dvh] w-full rounded-none pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-h-[calc(100dvh-32px)] sm:w-[calc(100%-32px)] sm:max-w-[var(--dlg-w)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:pt-0 sm:pb-0'
}

/** Open dialogs, oldest first: the last one owns Escape and Tab. */
const openDialogs: object[] = []

/**
 * Modal surface with focus containment, Escape to close and focus return.
 * `side="right"` renders a drawer (a bottom sheet under 640px), `side="bottom"` a bottom sheet;
 * a centered dialog is full screen under 640px.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  side = 'center',
  width = 520,
  footer,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  side?: DialogSide
  width?: number
  footer?: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const descId = useId()
  // The latest onClose without re-running the effect: callers pass an inline function, and re-running on every
  // parent render moved focus out of the field being typed in (V-R5).
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useEffect(() => {
    if (!open) return
    const prev = document.activeElement as HTMLElement | null
    const el = ref.current
    const token = {}
    openDialogs.push(token)
    const focusables = () => Array.from(el?.querySelectorAll<HTMLElement>('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])') ?? [])
    ;(focusables()[0] ?? el)?.focus()
    const onKey = (e: KeyboardEvent) => {
      // Only the top dialog answers the keyboard: Escape closes one layer, Tab stays in the top one.
      if (openDialogs[openDialogs.length - 1] !== token) return
      if (e.key === 'Escape') {
        e.stopPropagation()
        onCloseRef.current()
      } else if (e.key === 'Tab') {
        const f = focusables()
        if (!f.length) return
        const first = f[0]!
        const last = f[f.length - 1]!
        const active = document.activeElement
        if (!el?.contains(active)) {
          e.preventDefault()
          ;(e.shiftKey ? last : first).focus()
        } else if (e.shiftKey && active === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && active === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      openDialogs.splice(openDialogs.indexOf(token), 1)
      document.body.style.overflow = overflow
      prev?.focus?.()
    }
  }, [open])
  if (!open) return null
  const sheet = side !== 'center'
  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-overlay" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        data-side={side}
        style={{ '--dlg-w': `${width}px` } as CSSProperties}
        className={cx('rh-overlay absolute flex flex-col outline-none', dialogPanelClasses(side))}
      >
        {sheet && <div aria-hidden="true" className={cx('mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-line-strong', side === 'right' && 'sm:hidden')} />}
        <div className={cx('flex items-start justify-between gap-4 border-b border-line px-5 pb-4', sheet ? 'pt-3 sm:pt-5' : 'pt-4 sm:pt-5')}>
          <div className="min-w-0">
            <h2 id={titleId} className="font-display text-[18px] font-semibold leading-tight text-ink">
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-1 text-[13px] text-ink-3">
                {description}
              </p>
            )}
          </div>
          <IconButton icon="close" label="Close" onClick={onClose} className="-mr-1 shrink-0" size={40} />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">{children}</div>
        {footer && <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}
